import React from 'react';
import dayjs from 'dayjs';
import type { ServiceReceiptDTO } from '@/services/serviceReceipt.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { firstNonUuidDisplay } from '@/utils/displayLabels';

export interface PrintableServiceReceiptProps {
  sr?: ServiceReceiptDTO;
  data?: ServiceReceiptDTO;
  receipt?: ServiceReceiptDTO;
  onClose?: () => void;
}

function amt(v: unknown): string {
  const n = Number(v ?? 0);
  return (Number.isNaN(n) ? 0 : n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Service receipt — shared pagination engine with restored meta/values. */
export const PrintableServiceReceipt: React.FC<PrintableServiceReceiptProps> = (props) => {
  const sr = (props.sr || props.data || props.receipt || {}) as ServiceReceiptDTO;
  const milestone =
    sr.milestone_label ||
    (sr.milestone ? `#${sr.milestone.milestone_number} ${sr.milestone.milestone_name}` : '');
  const supplierName = sr.supplier_name || sr.supplier?.supplier_name || '';
  const poNumber = sr.po_number || sr.purchaseOrder?.po_number;

  return (
    <EngineCommercialA4Print
      docType="service_receipt"
      title="SERVICE RECEIPT"
      documentNumber={sr.sr_number || 'SR'}
      partyName={supplierName}
      onClose={props.onClose}
      metaLines={[
        { label: 'SR No', value: sr.sr_number || '' },
        {
          label: 'Date',
          value: sr.sr_date ? dayjs(sr.sr_date).format('DD MMM YYYY') : '',
        },
        { label: 'PO', value: firstNonUuidDisplay(poNumber, { empty: '' }) },
        { label: 'Milestone', value: firstNonUuidDisplay(milestone, { empty: '' }) },
        { label: 'Supplier', value: supplierName },
        { label: 'Provider reference', value: sr.service_provider_reference || '' },
        { label: 'Status', value: sr.status || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Description' },
        { key: 'value', label: 'Value', align: 'right' },
      ]}
      lines={[
        {
          lineNumber: 1,
          description: 'Claimed value',
          cells: { value: amt(sr.claimed_value) },
        },
        {
          lineNumber: 2,
          description: 'Approved value',
          cells: { value: amt(sr.approved_value) },
        },
      ]}
      finals={sr.notes ? [{ id: 'notes', heightPx: 40 }] : []}
      finalBlocks={
        sr.notes
          ? {
              notes: (
                <p>
                  <strong>Notes:</strong> {sr.notes}
                </p>
              ),
            }
          : undefined
      }
    />
  );
};

export default PrintableServiceReceipt;
